import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext, Store } from '@ngxs/store'
import {
  AddItemToCartAction,
  RemoveItemToCartAction,
  ResetCartStateAction,
  SetItemsToCartAction,
  UpdateCartItemQuantityAction,
} from './cart.meta.actions'
import { CartMetaStateModel } from './cart.meta.model'
import { Utils } from 'src/helpers/utils.helper'
import { KitType } from '../quote/quote.meta.model'
import {
  ResetCheckoutKitTypeAction,
  SetCheckoutKitTypeAction,
} from '../quote/quote.meta.actions'
import { QuoteMetaState } from '../quote/quote.meta.state'

const INITIAL_STATE: CartMetaStateModel = {
  items: [],
  type: null,
}

export const cartLocalStorageKey = `byd-accredited-1.0.0@cart`

@State<CartMetaStateModel>({
  name: 'cartMeta',
  defaults: { ...INITIAL_STATE },
})
@Injectable()
export class CartMetaState {
  private cartTypeMessages = {
    [KitType.CUSTOM]:
      'You already have a customized kit in your cart, do you want to clear the items to continue?',
    [KitType.STANDARD]:
      'You already have a standard kit in your cart, do you want to clear the items to continue?',
    [KitType.CHARGER]:
      'You already have a charger in your cart, do you want to clear the items to continue?',
  }

  constructor(
    private utils: Utils,
    private store: Store,
  ) {}

  @Selector()
  public static getState(state: CartMetaStateModel): CartMetaStateModel {
    return state
  }

  @Selector()
  public static getItems(state: CartMetaStateModel): any[] {
    return state.items
  }

  @Selector()
  public static getTotal(state: CartMetaStateModel): number {
    return state.items.reduce(
      (acc, item) => acc + item.value * item.quantity,
      0,
    )
  }

  @Selector()
  public static getCartType(state: CartMetaStateModel): KitType {
    return state.type
  }

  @Action(ResetCartStateAction)
  resetState(ctx: StateContext<CartMetaStateModel>): void {
    this.store.dispatch(new ResetCheckoutKitTypeAction())
    ctx.setState({ ...INITIAL_STATE })
    this.clearItems()
  }

  @Action(AddItemToCartAction)
  async addItem(
    ctx: StateContext<CartMetaStateModel>,
    action: AddItemToCartAction,
  ) {
    const state = ctx.getState()
    const { item: payloadItem, type: payloadType } = action.payload
    const kitType = this.store.selectSnapshot(
      QuoteMetaState.getCheckoutDataKitType,
    )
    const isSameKitType = kitType === payloadType
    if (!isSameKitType) {
      this.store.dispatch(new SetCheckoutKitTypeAction(payloadType))
    }

    if (payloadType !== state.type && state.type !== null) {
      await this.utils
        .showConfirmMessage(
          'Different type of items',
          this.cartTypeMessages[state.type],
        )
        .then((result) => {
          if (result.isConfirmed) {
            ctx.patchState({ items: [payloadItem], type: payloadType })
            this.persistItems({ items: [payloadItem], type: payloadType })
          }
        })
      return
    }

    const found = state.items.find((item) => item.id === payloadItem.id)
    const previousItems = [...state.items]

    if (found) {
      previousItems.forEach((item) => {
        if (found.id === item.id) {
          item.quantity = found.quantity + payloadItem.quantity
        }
      })
      const newState = { ...state, items: previousItems }
      ctx.patchState(newState)
      this.persistItems(newState)

      return
    }

    const newState = {
      type: payloadType,
      items: [
        ...previousItems,
        {
          ...action.payload.item,
          quantity: action.payload.item.quantity || 1,
        },
      ],
    }
    ctx.patchState(newState)
    this.persistItems(newState)
  }

  @Action(RemoveItemToCartAction)
  removeItem(
    ctx: StateContext<CartMetaStateModel>,
    action: RemoveItemToCartAction,
  ): void {
    const state = ctx.getState()
    const items = state.items.filter((item) => item.id !== action.payload.id)
    const type = items.length ? state.type : null
    const newState = { items, type }

    if (!type) {
      this.store.dispatch(new ResetCheckoutKitTypeAction())
    }

    ctx.patchState(newState)
    this.persistItems(newState)
  }

  @Action(SetItemsToCartAction)
  setItems(
    ctx: StateContext<CartMetaStateModel>,
    action: SetItemsToCartAction,
  ): void {
    this.store.dispatch(new SetCheckoutKitTypeAction(action.payload.type))
    ctx.patchState(action.payload)
    this.persistItems(action.payload)
  }

  @Action(UpdateCartItemQuantityAction)
  updateItemQuantity(
    ctx: StateContext<CartMetaStateModel>,
    action: UpdateCartItemQuantityAction,
  ): void {
    const state = ctx.getState()
    const items = [...state.items]
    items.forEach((item) => {
      if (item.id === action.payload.id) {
        item.quantity = action.payload.quantity
      }
    })
    ctx.patchState({ ...state, items })
    this.persistItems({ ...state, items })
  }

  persistItems(cart: CartMetaStateModel): void {
    localStorage.setItem(cartLocalStorageKey, JSON.stringify(cart))
  }

  clearItems(): void {
    localStorage.removeItem(cartLocalStorageKey)
  }
}
